import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createGameSession,
  joinGameSession,
  submitGameAction,
  getGameSession,
  getWaitingGames,
} from '../api/game';
import { GameSession, CreateGameRequest, JoinGameRequest, GameAction } from '../types/game';
import { supabase } from '../lib/supabaseClient';

export function useGameSession(gameSessionId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery<GameSession | null>({
    queryKey: ['game-session', gameSessionId],
    queryFn: () => (gameSessionId ? getGameSession(gameSessionId) : null),
    enabled: !!gameSessionId,
    refetchInterval: 30000, // 30초마다 폴링 (Realtime fallback)
  });

  // Realtime 구독으로 즉시 업데이트
  useEffect(() => {
    if (!gameSessionId) return;

    const channel = supabase
      .channel(`game-session:${gameSessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'game_sessions',
          filter: `id=eq.${gameSessionId}`,
        },
        (payload) => {
          // React Query 캐시 즉시 업데이트
          const updatedSession = payload.new as GameSession;
          queryClient.setQueryData<GameSession | null>(
            ['game-session', gameSessionId],
            updatedSession
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [gameSessionId, queryClient]);

  return query;
}

export function useWaitingGames(enabled: boolean = true) {
  return useQuery<GameSession[]>({
    queryKey: ['waiting-games'],
    queryFn: getWaitingGames,
    enabled,
    refetchInterval: enabled ? 5000 : false, // 활성화된 경우에만 5초마다 갱신
  });
}

export function useCreateGame() {
  const queryClient = useQueryClient();

  return useMutation<GameSession, Error, CreateGameRequest>({
    mutationFn: createGameSession,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['waiting-games'] });
      queryClient.setQueryData(['game-session', data.id], data);
    },
  });
}

export function useJoinGame() {
  const queryClient = useQueryClient();

  return useMutation<GameSession, Error, JoinGameRequest>({
    mutationFn: joinGameSession,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['waiting-games'] });
      queryClient.setQueryData(['game-session', data.id], data);
    },
  });
}

export function useGameAction() {
  const queryClient = useQueryClient();

  return useMutation<GameSession, Error, { gameSessionId: string; action: GameAction }>({
    mutationFn: async ({ gameSessionId, action }) => {
      // 동시성 충돌 시 최대 3회 재시도
      let lastError: Error | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          return await submitGameAction(gameSessionId, action);
        } catch (error: any) {
          lastError = error;
          // 409 Conflict는 동시성 충돌, 재시도
          if (error?.status === 409 || error?.message?.includes('updated by another action')) {
            if (attempt < 2) {
              // 최신 게임 상태를 다시 가져옴
              await queryClient.invalidateQueries({ queryKey: ['game-session', gameSessionId] });
              // 짧은 딜레이 후 재시도
              await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)));
              continue;
            }
          }
          throw error;
        }
      }
      throw lastError || new Error('Failed to submit game action');
    },
    onSuccess: (data) => {
      // 캐시 업데이트 및 강제 리페치로 최신 데이터 보장
      queryClient.setQueryData(['game-session', data.id], data);
      queryClient.invalidateQueries({ queryKey: ['game-session', data.id] });
    },
  });
}

